// Position-level Greeks aggregation for production collar hedging.
//
// Input units (per the chain's OptionRow):
//   delta : per share, signed (call +, put −), magnitude 0..1
//   gamma : per share per $1 spot move (always +)
//   vega  : per share per 1% IV move (always +)
//   theta : per share per 1 day (typically − for long options)
//
// Output units (production risk-report convention):
//   shareDelta  : SHARE-equivalent delta (dimensionless count of shares)
//                 e.g., +10,000 long stock with a collar might net to +4,500
//   dollarDelta : shareDelta × spot — the $ value move per $1 spot tick
//   shareGamma  : change in shareDelta per $1 spot move (signed; short
//                 options contribute negative gamma)
//   pctGamma    : shareGamma × spot / 100 — share-delta change per 1% spot move
//                 (the more useful number for hedgers thinking in % terms)
//   dollarVega  : $ position-value change per 1pp IV move
//   dollarTheta : $ position-value change per 1 calendar day
//
// Each option contract represents 100 underlying shares. Position multiplier
// is `contracts × 100 × legSign`, where legSign is +1 for long, −1 for short.

import type { HedgeLeg } from './types';

export interface AggregatedGreeks {
  shareDelta: number;
  dollarDelta: number;
  shareGamma: number;
  pctGamma: number;
  dollarVega: number;
  dollarTheta: number;
}

export interface PositionGreeks {
  shares: number;
  spot: number;
  /** Sum of stock + every option leg. */
  total: AggregatedGreeks;
  /** Stock-only (the bare long position). */
  stock: AggregatedGreeks;
  /** Sum across option legs (the collar contribution). */
  options: AggregatedGreeks;
  /** Per-leg breakdown so the UI can show contribution attribution. */
  perLeg: Array<{ leg: HedgeLeg; contracts: number; greeks: AggregatedGreeks }>;
}

export const CONTRACT_MULTIPLIER = 100;

function zeroGreeks(): AggregatedGreeks {
  return {
    shareDelta: 0, dollarDelta: 0,
    shareGamma: 0, pctGamma: 0,
    dollarVega: 0, dollarTheta: 0,
  };
}

function addGreeks(a: AggregatedGreeks, b: AggregatedGreeks): AggregatedGreeks {
  return {
    shareDelta: a.shareDelta + b.shareDelta,
    dollarDelta: a.dollarDelta + b.dollarDelta,
    shareGamma: a.shareGamma + b.shareGamma,
    pctGamma: a.pctGamma + b.pctGamma,
    dollarVega: a.dollarVega + b.dollarVega,
    dollarTheta: a.dollarTheta + b.dollarTheta,
  };
}

/** Per-share delta for a stock long. Always +1 (delta of underlying = 1). */
function stockGreeks(shares: number, spot: number): AggregatedGreeks {
  return {
    shareDelta: shares,
    dollarDelta: shares * spot,
    shareGamma: 0,
    pctGamma: 0,
    dollarVega: 0,
    dollarTheta: 0,
  };
}

/** Per-leg position-level Greeks at a given contract count.
 *
 *  Leg sign: action 'buy' = +1 (long, you own it), 'sell' = −1 (short, you wrote it).
 *  Long short-call-gamma is what makes a collar short-gamma overall — even
 *  though gamma per share is always positive, a SHORT call contributes
 *  negative position-gamma. */
export function legGreeks(leg: HedgeLeg, contracts: number, spot: number): AggregatedGreeks {
  if (leg.asset === 'stock') {
    // Caller should use stockGreeks for the long position. A stock leg in the
    // strategy is typically a trim (action='sell'), modelled per-share.
    const sign = leg.action === 'sell' ? -1 : 1;
    const shares = contracts;
    return stockGreeks(sign * shares, spot);
  }

  const sign = leg.action === 'buy' ? 1 : -1;
  const multiplier = contracts * CONTRACT_MULTIPLIER * sign;

  const delta = leg.delta ?? 0;
  // The chain doesn't currently surface gamma/vega/theta on HedgeLeg — they
  // live on the raw OptionRow. callers that want full Greeks must pass them
  // through via leg.bid / etc., or use legGreeksFromRow below.
  return {
    shareDelta: multiplier * delta,
    dollarDelta: multiplier * delta * spot,
    shareGamma: 0,
    pctGamma: 0,
    dollarVega: 0,
    dollarTheta: 0,
  };
}

/** Variant that takes per-share gamma/vega/theta explicitly — the production path.
 *  Use this when you have the full OptionRow (which the chain always provides). */
export function legGreeksFull(
  leg: HedgeLeg,
  contracts: number,
  spot: number,
  perShare: { delta: number; gamma: number; vega: number; theta: number },
): AggregatedGreeks {
  if (leg.asset === 'stock') {
    const sign = leg.action === 'sell' ? -1 : 1;
    const shares = contracts;
    return stockGreeks(sign * shares, spot);
  }
  const sign = leg.action === 'buy' ? 1 : -1;
  const multiplier = contracts * CONTRACT_MULTIPLIER * sign;
  const shareGamma = multiplier * perShare.gamma;
  return {
    shareDelta: multiplier * perShare.delta,
    dollarDelta: multiplier * perShare.delta * spot,
    shareGamma,
    pctGamma: shareGamma * spot / 100,
    // Chain vega is per 1% IV move (= 0.01 in vol units). Position $ vega is
    // contracts × 100 × perShareVega.
    dollarVega: multiplier * perShare.vega,
    dollarTheta: multiplier * perShare.theta,
  };
}

/** Aggregate a long stock position + a set of option legs at a uniform contract
 *  count. For asymmetric structures (different call vs put contract counts)
 *  use buildPositionGreeksAsymmetric. */
export function buildPositionGreeks(args: {
  shares: number;
  spot: number;
  legs: HedgeLeg[];
  contracts: number;
  perShareGreeks: Array<{ delta: number; gamma: number; vega: number; theta: number } | null>;
}): PositionGreeks {
  const { shares, spot, legs, contracts, perShareGreeks } = args;
  const stock = stockGreeks(shares, spot);
  let options = zeroGreeks();
  const perLeg: PositionGreeks['perLeg'] = [];
  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i];
    const ps = perShareGreeks[i];
    const g = ps
      ? legGreeksFull(leg, contracts, spot, ps)
      : legGreeks(leg, contracts, spot);
    options = addGreeks(options, g);
    perLeg.push({ leg, contracts, greeks: g });
  }
  return { shares, spot, stock, options, total: addGreeks(stock, options), perLeg };
}

/** Asymmetric variant — each option leg gets its own contract count. Use this
 *  when you've split the call ratio from the put ratio (e.g., over-write calls
 *  to fund deeper put protection, common production move). */
export function buildPositionGreeksAsymmetric(args: {
  shares: number;
  spot: number;
  legs: HedgeLeg[];
  contractsPerLeg: number[];
  perShareGreeks: Array<{ delta: number; gamma: number; vega: number; theta: number } | null>;
}): PositionGreeks {
  const { shares, spot, legs, contractsPerLeg, perShareGreeks } = args;
  if (legs.length !== contractsPerLeg.length) {
    throw new Error(`legs.length=${legs.length} but contractsPerLeg.length=${contractsPerLeg.length}`);
  }
  const stock = stockGreeks(shares, spot);
  let options = zeroGreeks();
  const perLeg: PositionGreeks['perLeg'] = [];
  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i];
    const ps = perShareGreeks[i];
    const c = contractsPerLeg[i];
    const g = ps
      ? legGreeksFull(leg, c, spot, ps)
      : legGreeks(leg, c, spot);
    options = addGreeks(options, g);
    perLeg.push({ leg, contracts: c, greeks: g });
  }
  return { shares, spot, stock, options, total: addGreeks(stock, options), perLeg };
}

/** Compute the contract count needed to drive net position delta to a target
 *  fraction of the unhedged stock delta.
 *
 *  - targetDeltaFraction = 1.0 → no hedge (leave full long delta)
 *  - targetDeltaFraction = 0.5 → hedge half the delta
 *  - targetDeltaFraction = 0.0 → delta-neutral
 *
 *  Returns null if the option legs don't reduce delta (would require an
 *  infinite or negative contract count). */
export function solveContractsForTargetDelta(args: {
  shares: number;
  legs: HedgeLeg[];
  perShareDeltas: Array<number | null>;
  targetDeltaFraction: number;
}): number | null {
  const { shares, legs, perShareDeltas, targetDeltaFraction } = args;
  // Per-contract net option delta:
  // For each leg, sign × CONTRACT_MULTIPLIER × per-share delta
  let perContractOptionDelta = 0;
  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i];
    if (leg.asset === 'stock') continue;
    const ps = perShareDeltas[i];
    if (ps == null) return null;
    const sign = leg.action === 'buy' ? 1 : -1;
    perContractOptionDelta += sign * CONTRACT_MULTIPLIER * ps;
  }
  if (perContractOptionDelta >= 0) {
    // Options don't reduce delta (or even add to it). No solution.
    return null;
  }
  const currentStockDelta = shares;
  const targetTotalDelta = currentStockDelta * targetDeltaFraction;
  // currentStockDelta + contracts × perContractOptionDelta = targetTotalDelta
  // contracts = (targetTotalDelta − currentStockDelta) / perContractOptionDelta
  const contracts = (targetTotalDelta - currentStockDelta) / perContractOptionDelta;
  return contracts;
}

/** Delta drift: how net position delta moves as spot moves. Linear
 *  approximation using gamma — for production this is fine for ±10%
 *  scenarios, less accurate for tail moves where gamma itself changes.
 *
 *  Returns rows for spot moves [-10%, -5%, 0, +5%, +10%, ...] (or whatever
 *  offsets are supplied) with net share-delta at each scenario. */
export function buildDeltaDriftTable(
  greeks: PositionGreeks,
  spotOffsets: number[] = [-0.15, -0.10, -0.05, 0, 0.05, 0.10, 0.15],
): Array<{ spotOffset: number; spot: number; shareDelta: number }> {
  const baseSpot = greeks.spot;
  const baseDelta = greeks.total.shareDelta;
  const gamma = greeks.total.shareGamma;
  return spotOffsets.map(off => {
    const spot = baseSpot * (1 + off);
    const dSpot = spot - baseSpot;
    const shareDelta = baseDelta + gamma * dSpot;
    return { spotOffset: off, spot, shareDelta };
  });
}
